import {Icon} from '@wandb/weave/components/Icon';
import React, {useContext, useState} from 'react';

import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../../components/Banner';
import {GlobalBannerContext} from '../../../../components/GlobalBanners';
import {OrganizationDataForFailedPaymentBanner} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {createGraphqlOrgId} from '../../../../util/id';
import {
  TeamOrgQueryResult,
  useCurrentTeamOrg,
} from '../../../../util/nudgeBars';
import {StripeElementsComp} from '../../../../util/pricing';
import {AddPaymentMethodModal} from '../../Checkout/AddPaymentMethodModal';
import * as S from './PaymentFailedBanner.styles';

interface PaymentFailedBannerProps {
  style?: React.CSSProperties;
}

export const PaymentFailedBanner = ({style}: PaymentFailedBannerProps) => {
  const currentTeamOrg: TeamOrgQueryResult | null = useCurrentTeamOrg();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const {states} = useContext(GlobalBannerContext);

  if (states == null) {
    return null;
  }

  const currentOrgName = currentTeamOrg?.entity.organization?.name;

  // If we are on an org specific page, then only show banner if data found in states for that org
  // else we are not on org specific page so show all the banners
  const organizationStatesToShow = currentOrgName
    ? states
        .filter(s => s?.failedPaymentOrgData?.orgName === currentOrgName)
        .map(
          s => s.failedPaymentOrgData as OrganizationDataForFailedPaymentBanner
        )
    : states
        .filter(s => s?.failedPaymentOrgData !== null)
        .map(
          s => s.failedPaymentOrgData as OrganizationDataForFailedPaymentBanner
        );

  // If there are no orgs that have failed payments then don't display banners
  if (organizationStatesToShow.length === 0) {
    return null;
  }

  return (
    <div style={style}>
      <S.AccountWarningMessages>
        {organizationStatesToShow.map((org, index) => {
          const bannerText = `Your payment method failed for ${org.orgName}.`;
          let buttonContent = null;
          let contactInfo;
          if (org?.isBillingUser) {
            buttonContent = (
              <S.RetryTargetBlank
                onClick={() => {
                  setIsPaymentModalOpen(true);
                  Analytics.track('Add Payment Method Clicked', {
                    location: 'payment failed warning banner',
                    organizationName: org,
                  });
                }}>
                Add Payment Method <S.AddPayment name="addPayment" />
              </S.RetryTargetBlank>
            );
            contactInfo = `Update your payment method to continue using wandb without disruption.`;
          } else {
            contactInfo = `Contact your admin, ${org.billingEmail}, to update your payment method to continue using wandb without disruption.`;
          }
          return (
            <React.Fragment key={index}>
              <Banner
                variant={'error'}
                eventData={{
                  location: 'payment failed warning banner',
                  organizationName: org.orgName,
                }}>
                <BannerFlexWrapper>
                  <Icon name="warning" />
                  <BannerTextWrapper>
                    <BannerBoldText>{bannerText}</BannerBoldText>
                    {contactInfo}
                  </BannerTextWrapper>
                </BannerFlexWrapper>
                {buttonContent}
              </Banner>
              {isPaymentModalOpen && (
                <StripeElementsComp>
                  <AddPaymentMethodModal
                    billingUserEmail={org.billingEmail}
                    organizationName={org.orgName}
                    organizationID={createGraphqlOrgId(org.orgId)}
                    shouldDefault={true}
                    onTransactionCompleted={() => setIsPaymentModalOpen(false)}
                    onClose={() => setIsPaymentModalOpen(false)}
                  />
                </StripeElementsComp>
              )}
            </React.Fragment>
          );
        })}
      </S.AccountWarningMessages>
    </div>
  );
};
